import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import CookieSettings from "./CookieSettings";
import { getConsent } from "@/lib/consent";

const studioLinks = [
  { text: "Work", href: "/" },
  { text: "Method", href: "/" },
  { text: "About", href: "/" },
  { text: "Teams", href: "/teams" },
  { text: "Careers", href: "/careers" },
];

const resourceLinks = [
  { text: "Blog", href: "/blog" },
  { text: "Research", href: "/research" },
  { text: "Lineage", href: "/lineage" },
];

const legalLinks = [
  { text: "Privacy Policy", href: "/privacy" },
  { text: "Cookie Policy", href: "/cookies" },
];

const socialLinks = [
  { text: "LinkedIn", href: "https://linkedin.com/company/forgivenesslabs" },
  { text: "X", href: "https://x.com/Forgivenesslabs" },
];

const SiteFooter = () => {
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  return (
    <>
      <footer className="border-t border-border/30 bg-background">
        {/* Main grid */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Column 1 — Brand */}
            <div className="lg:col-span-1">
              <h3 className="font-serif text-lg text-foreground mb-3">Forgiveness Labs</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-xs">
                An applied research studio building ethical systems.
              </p>
              <div className="flex items-center gap-4">
                {socialLinks.map(({ text, href }) => (
                  <a
                    key={text}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300"
                  >
                    {text}
                  </a>
                ))}
              </div>
            </div>

            {/* Column 2 — Studio */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Studio</h4>
              <ul className="space-y-2.5">
                {studioLinks.map(({ text, href }) => (
                  <li key={text}>
                    <Link
                      to={href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                    >
                      {text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 — Resources */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Resources</h4>
              <ul className="space-y-2.5">
                {resourceLinks.map(({ text, href }) => (
                  <li key={text}>
                    <Link
                      to={href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                    >
                      {text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4 — Contact */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="mailto:sup@forgivenesslabs.com"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                  >
                    <Mail className="w-3.5 h-3.5 shrink-0" />
                    sup@forgivenesslabs.com
                  </a>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>UK · US · Everywhere</span>
                </li>
                <li className="pt-1">
                  <Link
                    to="/contact"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                  >
                    Contact form →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/20">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Forgiveness Labs Ltd. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {legalLinks.map(({ text, href }) => (
                <Link
                  key={text}
                  to={href}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-300"
                >
                  {text}
                </Link>
              ))}
              <button
                onClick={() => setShowCookieSettings(true)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-300 bg-transparent border-none cursor-pointer"
              >
                Cookie settings
              </button>
              <Link
                to="/workers"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-300"
              >
                Staff →
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <CookieSettings open={showCookieSettings} onOpenChange={setShowCookieSettings} currentStatus={getConsent()} />
    </>
  );
};

export default SiteFooter;
